import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import { useAuth } from '../../contexts/AuthContext';
import { useBrand } from '../../contexts/BrandContext';
import { reportsApi, fetchAgencyLogoDataUrl } from '../../services/reports';
import { tasksApi } from '../../services/tasks';
import type {
  ReportAxisKey,
  ReportSeries,
  ReportSeriesPoint,
  StatusReportPayload,
  Task,
} from '@traaker/shared';

type PresetKey = '30d' | '90d' | '180d' | '365d';

const PRESETS: Record<PresetKey, { label: string; days: number }> = {
  '30d': { label: '30 jours', days: 30 },
  '90d': { label: '3 mois', days: 90 },
  '180d': { label: '6 mois', days: 180 },
  '365d': { label: '12 mois', days: 365 },
};

const AXIS_META: Record<ReportAxisKey, { label: string; color: string }> = {
  website: { label: 'Website', color: '#F59E0B' },
  narrative: { label: 'Narrative', color: '#8B5CF6' },
  authority: { label: 'Authority', color: '#00DDB3' },
  referral: { label: 'Referral', color: '#FF6B35' },
  earned_media: { label: 'Earned Media', color: '#0EA5E9' },
};

const AXIS_KEYS: ReportAxisKey[] = ['website', 'narrative', 'authority', 'referral', 'earned_media'];

/**
 * Manual "Rapport d'activité" — score evolution over a date range,
 * completed tasks, ongoing tasks, and a curated 3/6/12-month focus
 * picked from the task menu.
 */
export default function StatusReport() {
  const { tenant } = useAuth();
  const { activeBrand } = useBrand();
  const [preset, setPreset] = useState<PresetKey>('90d');
  const [brandTasks, setBrandTasks] = useState<Task[]>([]);
  const [focusIds, setFocusIds] = useState<string[]>([]);
  const [payload, setPayload] = useState<StatusReportPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<'pdf' | 'pptx' | null>(null);
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);

  const exportAs = async (fmt: 'pdf' | 'pptx') => {
    if (!payload) return;
    setExporting(fmt);
    try {
      const mod = await import('../../services/reportExport');
      if (fmt === 'pdf') await mod.downloadStatusReportPdf(payload, logoDataUrl);
      else await mod.downloadStatusReportPptx(payload, logoDataUrl);
    } catch (err: any) {
      // Stale SPA tab: a new deploy replaced the chunk hash, so dynamic
      // import() now 404s. Force-reload picks up the fresh index.html.
      if (isChunkLoadError(err)) {
        window.location.reload();
        return;
      }
      setError(err?.message || 'Export impossible');
    } finally {
      setExporting(null);
    }
  };

  const reportsEnabled = !!tenant?.settings?.featureFlags?.reportsEnabled;
  const tasksEnabled = !!tenant?.settings?.featureFlags?.tasksEnabled;
  const hasLogo = !!tenant?.settings?.agencyBranding?.logoUrl;

  useEffect(() => {
    if (!reportsEnabled || !hasLogo) {
      setLogoDataUrl(null);
      return;
    }
    void fetchAgencyLogoDataUrl().then(setLogoDataUrl);
  }, [reportsEnabled, hasLogo]);

  // Load open tasks for the focus picker — cheap, same endpoint as /tasks.
  useEffect(() => {
    if (!reportsEnabled || !activeBrand || !tasksEnabled) return;
    tasksApi
      .list(activeBrand.brandId)
      .then((all) =>
        setBrandTasks(all.filter((t) => t.status === 'todo' || t.status === 'in_progress')),
      )
      .catch(() => setBrandTasks([]));
  }, [reportsEnabled, tasksEnabled, activeBrand?.brandId]);

  const dateWindow = useMemo(() => {
    const end = new Date();
    const start = new Date(end.getTime() - PRESETS[preset].days * 24 * 3600 * 1000);
    return { windowStart: start.toISOString(), windowEnd: end.toISOString() };
  }, [preset]);

  const generate = () => {
    if (!activeBrand) return;
    setLoading(true);
    setError(null);
    reportsApi
      .status({
        brandId: activeBrand.brandId,
        windowStart: dateWindow.windowStart,
        windowEnd: dateWindow.windowEnd,
        focusTaskIds: focusIds,
      })
      .then(setPayload)
      .catch((err) => setError(err?.message || 'Impossible de charger le rapport'))
      .finally(() => setLoading(false));
  };

  if (!reportsEnabled) return <DisabledNotice />;

  if (!activeBrand) {
    return (
      <Box>
        <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
          Rapport d'activité
        </Typography>
        <Alert severity="info">Sélectionnez une marque pour générer le rapport.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h4" fontWeight={700}>Rapport d'activité</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {logoDataUrl && (
            <Box
              component="img"
              src={logoDataUrl}
              alt={payload?.branding.agencyName || 'Agency logo'}
              sx={{ maxHeight: 36, maxWidth: 120, objectFit: 'contain' }}
            />
          )}
          {payload?.branding.agencyName && (
            <Typography variant="body2" color="text.secondary">{payload.branding.agencyName}</Typography>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            disabled={!payload || exporting !== null}
            onClick={() => exportAs('pdf')}
          >
            {exporting === 'pdf' ? 'Export…' : 'PDF'}
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<SlideshowIcon />}
            disabled={!payload || exporting !== null}
            onClick={() => exportAs('pptx')}
          >
            {exporting === 'pptx' ? 'Export…' : 'PPTX'}
          </Button>
        </Stack>
      </Stack>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Évolution des scores et actions réalisées pour {activeBrand.name}.
      </Typography>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Période</InputLabel>
                <Select
                  value={preset}
                  label="Période"
                  onChange={(e) => setPreset(e.target.value as PresetKey)}
                >
                  {Object.entries(PRESETS).map(([k, v]) => (
                    <MenuItem key={k} value={k}>{v.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FocusTaskPicker
                tasks={brandTasks}
                value={focusIds}
                onChange={setFocusIds}
                disabled={!tasksEnabled}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Button variant="contained" onClick={generate} disabled={loading} fullWidth>
                Générer
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {payload && (
        <Stack spacing={2}>
          <EvolutionGrid series={payload.series} />
          <TasksSection title={`Tâches terminées (${payload.tasksCompleted.length})`} tasks={payload.tasksCompleted} color="success.main" />
          <TasksSection title={`Tâches en cours (${payload.tasksInProgress.length})`} tasks={payload.tasksInProgress} color="warning.main" />
          {payload.focusTasks.length > 0 && (
            <TasksSection title={`Focus ${PRESETS[preset].label}`} tasks={payload.focusTasks} color="primary.main" />
          )}
          {payload.branding.footerText && (
            <Typography variant="caption" color="text.secondary" sx={{ pt: 2, textAlign: 'center' }}>
              {payload.branding.footerText}
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
}

function EvolutionGrid({ series }: { series: ReportSeries }) {
  return (
    <Grid container spacing={2}>
      {AXIS_KEYS.map((axis) => {
        const meta = AXIS_META[axis];
        const points = series[axis];
        const first = points[0]?.score;
        const last = points[points.length - 1]?.score;
        const delta = first != null && last != null ? last - first : null;
        return (
          <Grid size={{ xs: 12, md: 6, lg: 4 }} key={axis}>
            <Card variant="outlined" sx={{ borderLeft: `4px solid ${meta.color}` }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>{meta.label}</Typography>
                  {delta != null && (
                    <Chip
                      size="small"
                      label={`${delta >= 0 ? '+' : ''}${Math.round(delta)}`}
                      color={delta >= 0 ? 'success' : 'error'}
                      variant="outlined"
                    />
                  )}
                </Stack>
                {points.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Pas d'audit sur la période.
                  </Typography>
                ) : (
                  <>
                    <Sparkline points={points} color={meta.color} />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      {points.length} point{points.length > 1 ? 's' : ''} · dernier{' '}
                      <b>{last != null ? Math.round(last) : '—'}</b>
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
}

function Sparkline({ points, color }: { points: ReportSeriesPoint[]; color: string }) {
  const w = 240;
  const h = 60;
  if (points.length === 0) return null;
  if (points.length === 1) {
    const y = h - (points[0].score / 100) * h;
    return (
      <svg width="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        <circle cx={w / 2} cy={y} r={3} fill={color} />
      </svg>
    );
  }
  const xs = points.map((_, i) => (i / (points.length - 1)) * w);
  const ys = points.map((p) => h - (Math.max(0, Math.min(100, p.score)) / 100) * h);
  const d = xs.map((x, i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${ys[i].toFixed(1)}`).join(' ');
  return (
    <svg width="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      <path d={d} stroke={color} strokeWidth={2} fill="none" />
    </svg>
  );
}

function FocusTaskPicker({
  tasks,
  value,
  onChange,
  disabled,
}: {
  tasks: Task[];
  value: string[];
  onChange: (ids: string[]) => void;
  disabled: boolean;
}) {
  if (disabled) {
    return (
      <TextField
        size="small"
        fullWidth
        value=""
        label="Focus (module Tâches désactivé)"
        disabled
      />
    );
  }
  return (
    <FormControl fullWidth size="small">
      <InputLabel>Focus (tâches priorisées)</InputLabel>
      <Select
        multiple
        value={value}
        onChange={(e) => onChange(typeof e.target.value === 'string' ? [] : e.target.value)}
        label="Focus (tâches priorisées)"
        renderValue={(selected) => `${(selected as string[]).length} tâche(s)`}
      >
        {tasks.length === 0 && <MenuItem disabled>Aucune tâche active</MenuItem>}
        {tasks.map((t) => (
          <MenuItem key={t.taskId} value={t.taskId}>
            <Stack>
              <Typography variant="body2">{t.title}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t.priority} · {t.personaTab}
              </Typography>
            </Stack>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function TasksSection({ title, tasks, color }: { title: string; tasks: Task[]; color: string }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5, color }}>{title}</Typography>
        {tasks.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Aucune tâche.</Typography>
        ) : (
          <Stack spacing={1}>
            {tasks.map((t) => (
              <Box key={t.taskId} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: color,
                    mt: '8px',
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={500}>{t.title}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t.priority} · {t.category} · {new Date(t.updatedAt).toLocaleDateString('fr-FR')}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

function isChunkLoadError(err: any): boolean {
  const msg = String(err?.message || err || '');
  return (
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /ChunkLoadError/i.test(msg) ||
    err?.name === 'ChunkLoadError'
  );
}

function DisabledNotice() {
  return (
    <Box>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
        Rapport d'activité
      </Typography>
      <Alert severity="info">
        Les rapports en marque blanche ne sont pas activés pour votre organisation.
        Contactez votre administrateur Traaker pour y accéder.
      </Alert>
    </Box>
  );
}
